import {ButtonGroup, Modal} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {usePromotionList} from '@/hooks/usePromotionList'
import {updatePromotionStatus} from '@/services/PromotionsService/promotions'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useQueryClient} from '@tanstack/react-query'
import {useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'

export const PromotionActions = ({promotionData}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const {handleUpdatePromotion} = usePromotion()
  const {onDownloadBtnClick} = usePromotionList()
  const countryCode = useSelector(selectCountryCode)
  const isMexico = countryCode === 'MX'
  const [modalOpen, setModalOpen] = useState(false)
  const promotionId = promotionData?.id
  const promotionStatus = promotionData?.status
  const publishStatusAction = promotionStatus === 'UNPUBLISHED' || promotionStatus == 'DRAFT' ? 'Publish' : 'Unpublish'
  const publishActionIcon = promotionStatus === 'PUBLISHED' ? 'pause' : 'refresh'
  const isExpired = promotionStatus === 'EXPIRED'

  const toggleStatus = promotionStatus === 'PUBLISHED' ? 'UNPUBLISHED' : 'PUBLISHED'

  const handleUpdatePromotionStatus = async () => {
    if (!promotionId) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Promotion ID is missing.',
          type: 'error',
        })
      )
      return
    }

    try {
      await updatePromotionStatus(promotionId, toggleStatus)
      // Invalidate the query to trigger a refetch of promotion data with the updated status
      await queryClient.invalidateQueries({queryKey: ['PROMOTION_DETAIL', promotionId]})

      const successMessage =
        toggleStatus === 'PUBLISHED' ? (
          'Promotion Successfully Published'
        ) : (
          <div className="flex flex-col text-white">
            <strong>Promotion Unpublished</strong>
            <span>This promotion is now paused. You can reactivate it at any time.</span>
          </div>
        )
      const dispatchType = toggleStatus === 'PUBLISHED' ? 'success' : 'warning'

      dispatch(
        setToastNotifications({
          show: true,
          message: successMessage,
          type: dispatchType,
        })
      )
    } catch (error) {
      // Get the specific error message from the response
      let errorMessage = `Error updating promotion status to ${toggleStatus}`

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: errorMessage,
          type: 'error',
        })
      )
    }

    setModalOpen(false)
  }

  // Function to handle duplication of the promotion
  const handleDuplicatePromotion = () => {
    navigate(`/products/create-promotion/${promotionData?.id}`, {
      state: {
        promotionType: promotionData.promotionType || 'BUY_SINGLE_PRODUCT',
        promotionId: promotionData?.id,
        isDuplicating: true,
      },
    })
  }

  const publishOnClick = () => {
    if (promotionStatus === 'DRAFT') {
      handleUpdatePromotion({isCreating: true})
    } else {
      setModalOpen(true)
    }
  }

  const handleDownload = async () => {
    await onDownloadBtnClick(promotionId ? [promotionId] : [])
  }

  // Only show the publish button for Mexico as per requirements, but keep the duplicate and download buttons for all countries.
  const groupedButtons = [
    ...(isMexico && !isExpired
      ? [
          {
            icon: publishActionIcon,
            label: publishStatusAction,
            onClick: publishOnClick,
          },
        ]
      : []),
    {
      icon: 'copy',
      label: 'Duplicate',
      onClick: () => handleDuplicatePromotion(),
    },
    {
      icon: 'download',
      label: 'Download',
      onClick: () => handleDownload(),
    },
  ]

  const modalContent =
    promotionStatus === 'PUBLISHED'
      ? 'This promotion will be unpublished and will remain so until it is manually reactivated. Are you sure you want to proceed?'
      : 'This promotion will be published immediately. Are you sure you want to continue?'

  return (
    <>
      <ButtonGroup uiSchema={{items: groupedButtons}} />
      <Modal
        showModal={modalOpen}
        onCloseModal={() => setModalOpen(false)}
        title={`${publishStatusAction} Promotion`}
        type={MODAL_TYPE.SMALL}
        showSecondaryButton={true}
        secondaryButtonText="Cancel"
        onClickSecondaryButton={() => setModalOpen(false)}
        onClickPrimaryButton={handleUpdatePromotionStatus}
        primaryButtonText={publishStatusAction}
        content={<div className="text-white">{modalContent}</div>}
      />
    </>
  )
}
